import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import CursiveTool from '../components/tool/CursiveTool';
import Faq from '../components/Faq';
import CampaignDiscovery from '../components/campaign/CampaignDiscovery';
import StylePreviewCard, { sample, C } from '../components/StylePreviewCard';
import { T } from '../data/i18n';
import { HOME_FAQ } from '../data/mock';
import { flipText, combine, M } from '../lib/unicode';

export function PageHero({ t }) {
  const [title, ...subtitle] = t.h1.split('—');
  return (
    <section className="content-narrow page-hero fade-up">
      <h1 className="hero-title" data-testid="page-title">{title.trim()}
        {subtitle.length > 0 && <><span className="sr-only"> — </span><span className="hero-subtitle">{subtitle.join('—').trim()}</span></>}
      </h1>
      <p className="hero-intro">{t.intro}</p>
    </section>
  );
}

export function HowToSteps({ t }) {
  return (
    <section className="content-narrow pt-12">
      <h2 className="section-title text-center">{t.howTitle}</h2>
      <p className="text-center text-[15px] text-slate-600 mt-2">{t.howSub}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-7">
        {t.steps.map((s, i) => (
          <div key={i} className="editorial-card p-6" data-testid={`how-step-${i}`}>
            <span className="step-number" aria-hidden="true">0{i + 1}</span>
            <h3 className="text-[19px] font-medium text-[var(--ink)] mb-2">{s.t.replace(/^\d+-\s*/, '')}</h3>
            <p className="text-[14.5px] text-slate-600 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const CatRow = ({ title, items, card, reverse }) => (
  <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center py-6 ${reverse ? 'md:[&>*:first-child]:order-2' : ''}`}>
    <div className="prose-site">
      <h3>{title}</h3>
      <ul>{items.map(([b, d]) => <li key={b}><b>{b}</b> — {d}</li>)}</ul>
    </div>
    <div>{card}</div>
  </div>
);

export default function Home() {
  const t = T.en;
  return (
    <div data-testid="home-page">
      <PageHero t={t} />
      <div className="px-4"><CursiveTool t={t} /></div>
      <HowToSteps t={t} />

      <section className="content-narrow pt-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div className="prose-site">
          <h2 className="!mt-0">What Is a Cursive Text Generator?</h2>
          <p>A cursive text generator is a free browser tool that rewrites ordinary letters as flowing, handwriting-style characters. You type as usual and it instantly returns a version that looks like script, which you can try in compatible text fields. Some people call it a <Link to="/blogs/cursive-font-generator">cursive font generator</Link>.</p>
          <p>Nothing about your device fonts changes. Behind the scenes each letter is matched to a special Unicode symbol that happens to look like cursive. The result can keep its style in compatible bios, captions and chats, with no extra install. Restricted username fields or missing font glyphs may prevent it from working.</p>
        </div>
        <StylePreviewCard title="Live preview" rows={[
          ['Cursive Bold', sample(C.boldScript)], ['Cursive Normal', sample(C.script)], ['Bold Italic', sample(C.boldItalic)], ['Fraktur', sample(C.fraktur)],
        ]} />
      </section>

      <section className="content-narrow pt-10">
        <div className="prose-site">
          <h2>18 Cursive Font Styles in One Free Generator</h2>
          <p>Plenty of tools stop at one or two scripts. This one gives you eighteen separate looks, sorted into easy categories so you can find the right mood quickly.</p>
        </div>
        <CatRow title="Cursive Styles:" items={[
          ['Cursive Bold', 'heavy, confident script and the favourite for profile bios'],
          ['Cursive Normal', 'light and graceful classic handwriting'],
          ['Italic Serif', 'a bookish slant that suits formal messages'],
          ['Bold Italic', 'strong and expressive for headlines'],
          ['Sans Italic', 'a clean, modern lean for minimal feeds'],
          ['Sans Bold Italic', 'punchy modern slant that grabs attention'],
        ]} card={<StylePreviewCard title="Cursive" rows={[['Bold', sample(C.boldScript, 'Cursive Style')], ['Normal', sample(C.script, 'Cursive Style')], ['Italic', sample(C.italic, 'Cursive Style')]]} />} />
        <CatRow reverse title="Gothic / Fraktur Styles:" items={[
          ['Fraktur', 'medieval blackletter, a go-to for tattoo mock-ups'],
          ['Bold Fraktur', 'a denser gothic weight for dramatic titles'],
        ]} card={<StylePreviewCard accent="#6b3fa0" title="Gothic" rows={[['Fraktur', sample(C.fraktur, 'Gothic Style')], ['Bold Fraktur', sample(C.boldFraktur, 'Gothic Style')]]} />} />
        <CatRow title="Special & Decorative Styles:" items={[
          ['Double Struck', 'hollow, outlined letters'], ['Serif Bold', 'thick traditional serif'], ['Sans Bold', 'heavy modern capitals and lowercase'],
          ['Monospace', 'even, typewriter spacing'], ['Small Caps', 'compact capitals with quiet elegance'], ['Bubbles', 'letters inside circles'],
          ['Inverted', 'text turned upside down'], ['Strikethrough', 'a line through each letter'], ['Underline', 'a tidy line underneath'], ['Wavy Underline', 'a playful squiggle below'],
        ]} card={<StylePreviewCard accent="#0f8a78" title="Decorative" rows={[['Double', sample(C.doubleStruck, 'Special')], ['Bubbles', sample(C.circled, 'Special')], ['Inverted', flipText('Special')], ['Wavy', combine('Special', M.wavy)]]} />} />
      </section>

      <section className="content-narrow pt-4 pb-12 prose-site">
        <h2>Cursive Name Generator — Give Your Name Some Flair</h2>
        <p>Typing your own name is the most common way people use this tool. If you want something stranger than classic script, our <Link to="/weird-text-generator">weird text generator</Link> offers glitchy, boxed and runic looks too.</p>
        <p>Enter a first name, surname or full name and all eighteen versions appear together. Copy the one that feels right and paste it wherever you like. Popular places include:</p>
        <ul>
          {['Instagram and TikTok bios', 'X (Twitter) display names', 'Discord nicknames and server roles', 'Email signatures', 'Wedding and party invitations', 'Tattoo lettering ideas', 'Greeting cards and gift tags', 'YouTube channel names'].map((x) => <li key={x}>{x}</li>)}
        </ul>
        <p>There is no limit. Convert as many names as you want and copy each one instantly.</p>
        <Link to="/instagram-bio-generator" className="!no-underline flex items-center justify-between gap-4 rounded-lg bg-[var(--brand-soft)] border border-[var(--line)] px-5 py-4 my-4 transition-transform duration-300 hover:-translate-y-0.5" data-testid="home-bio-promo">
          <span><b className="text-slate-800">New: Instagram Bio Generator</b><br /><span className="text-[14px] text-slate-600">Ready-made bio templates with your name in cursive.</span></span>
          <span className="btn-gold !text-white shrink-0">Try it</span>
        </Link>

        <h2>Cursive Alphabet — A to Z in Script</h2>
        <p>Need single letters for a logo sketch, a lesson or a stencil? Type the alphabet and every style renders it at once. Here is the lowercase script set for quick reference:</p>
        <p className="text-[22px] !leading-relaxed text-slate-800 tracking-wide">{sample(C.script, 'a b c d e f g h i j k l m n o p q r s t u v w x y z')}</p>
        <p>These decorative Unicode forms are for digital previews. They do not replace handwriting instruction or a carefully prepared print design.</p>

        <h2>Why Choose This Cursive Generator?</h2>
        {[
          ['Browser-Based — Nothing to Install', 'The output is made of Unicode characters rather than a downloadable font, so it displays on iPhone, Android, Windows and Mac, and inside social apps, messengers and email.'],
          ['Free, With No Catch', 'No premium tier, no trial and no watermark. Every style and export is available to everyone, as often as needed.'],
          ['Real-Time Preview', 'Every card refreshes while you type. There is no generate button and no waiting.'],
          ['Export as an Image', 'Turn your styled text into a PNG with a choice of background colours, ready for stories, posts or print.'],
          ['Handy Extras', 'Switch letter case, reverse or space out your text, count words and characters, preview it on popular platforms and revisit your last six copies.'],
        ].map(([h3, d]) => (
          <div key={h3}>
            <h3 className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[var(--brand)]" />{h3}</h3>
            <p>{d}</p>
          </div>
        ))}
        <p>Prefer Spanish? Try our <Link to="/es/generador-de-texto-cursivo">letras cursivas</Link> page.</p>
      </section>

      <CampaignDiscovery />
      <Faq items={HOME_FAQ} title={t.faqTitle} />
    </div>
  );
}
